import Header from "../components/shared/Header";
import Footer from "../components/shared/Footer";
import SEOHead from "../components/shared/SEOHead";
import Breadcrumbs, { generateBreadcrumbSchema } from "../components/shared/Breadcrumbs";
import { Link } from "react-router-dom";
import { ArrowRight, Users, Award, Clock, Heart } from "lucide-react";
import { organizationSchema } from "../seo/schemas";

const AboutPage = () => {
  const stats = [
    { value: "10+", label: "Years Experience" },
    { value: "5,000+", label: "Happy Customers" },
    { value: "100%", label: "Satisfaction Rate" },
    { value: "24/7", label: "Support" }
  ];

  const values = [
    { icon: Users, title: "Customer First", desc: "Your satisfaction is our priority. We go above and beyond to ensure every move exceeds expectations." },
    { icon: Award, title: "Excellence", desc: "We take pride in our work, treating every item as if it were our own." },
    { icon: Clock, title: "Reliability", desc: "We arrive on time, every time. You can count on us when it matters most." },
    { icon: Heart, title: "Care", desc: "From first contact to final delivery, we handle your belongings with genuine care." }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="About Hitech Transit | Cardiff Removals Company"
        description="Founded in Cardiff, Hitech Transit has completed 5,000+ moves across Wales and England. 10+ years of experience, fully insured, customer-first approach. Meet the team."
        canonical="/about"
        jsonLd={[organizationSchema, generateBreadcrumbSchema([{ label: "About Us", href: "/about" }])]}
      />
      <Header />
      
      <main className="pt-20">
        <Breadcrumbs items={[{ label: "About Us", href: "/about" }]} />
        {/* Hero */}
        <section className="bg-[#0F172A] py-20">
          <div className="container-max px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl">
              <span className="text-xs tracking-[0.2em] uppercase font-bold text-[#FF5A1F] mb-4 block">
                About Us
              </span>
              <h1 className="text-4xl md:text-5xl font-['Outfit'] font-bold text-white tracking-tight mb-6">
                Moving People Forward
              </h1>
              <p className="text-slate-300 text-lg">
                Founded in Cardiff, Hitech Transit has grown to become one of the most trusted 
                removal companies serving Wales and England.
              </p>
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="bg-[#FF5A1F] py-12">
          <div className="container-max px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {stats.map((stat, index) => (
                <div key={stat.title || stat.name || `stat-${index}`} className="text-center">
                  <div className="text-4xl font-['Outfit'] font-black text-white mb-1">{stat.value}</div>
                  <div className="text-white/80 text-sm">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Story */}
        <section className="section-padding">
          <div className="container-max">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <span className="label-text">Our Story</span>
                <h2 className="text-3xl md:text-4xl font-['Outfit'] font-bold text-[#0F172A] mt-2 mb-6">
                  From Cardiff to the UK
                </h2>
                <p className="text-slate-600 leading-relaxed mb-6">
                  Hitech Transit started with a single van and a commitment to doing removals 
                  differently. We believed that moving home didn't have to be stressful, and 
                  that customers deserved transparent pricing and exceptional service.
                </p>
                <p className="text-slate-600 leading-relaxed mb-6">
                  Today, we operate a fleet of modern vehicles and employ a team of trained 
                  professionals across Cardiff and London. But our values remain the same: 
                  treat every customer's belongings as if they were our own.
                </p>
                <p className="text-slate-600 leading-relaxed">
                  We're proud to have moved thousands of families and businesses, building 
                  lasting relationships based on trust and reliability.
                </p>
              </div>
              <div>
                <img 
                  src="https://images.unsplash.com/photo-1585458808362-e5b83a52178e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBtb3ZlcnMlMjBwYWNraW5nJTIwYm94ZXN8ZW58MHx8fHwxNzc1NTg0NTMxfDA&ixlib=rb-4.1.0&q=85"
                  alt="Professional movers team"
                  className="w-full h-auto"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Values */}
        <section className="section-padding bg-[#F8F9FA]">
          <div className="container-max">
            <div className="text-center mb-12">
              <span className="label-text">Our Values</span>
              <h2 className="text-3xl md:text-4xl font-['Outfit'] font-bold text-[#0F172A] mt-2">
                What We Stand For
              </h2>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {values.map((value, index) => (
                <div key={value.title || value.name || `value-${index}`} className="text-center">
                  <div className="w-14 h-14 bg-[#FF5A1F]/10 mx-auto flex items-center justify-center mb-4">
                    <value.icon className="w-7 h-7 text-[#FF5A1F]" />
                  </div>
                  <h3 className="text-lg font-['Outfit'] font-bold text-[#0F172A] mb-2">{value.title}</h3>
                  <p className="text-slate-600 text-sm">{value.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="section-padding bg-[#0F172A]">
          <div className="container-max text-center">
            <h2 className="text-3xl md:text-4xl font-['Outfit'] font-bold text-white mb-6">
              Ready to Experience the Difference?
            </h2>
            <p className="text-slate-300 text-lg max-w-2xl mx-auto mb-8">
              Join thousands of satisfied customers who've trusted Hitech Transit with their moves.
            </p>
            <Link to="/instant-quote" className="btn-primary inline-flex items-center gap-2" data-testid="about-cta-btn">
              Get Your Quote
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default AboutPage;
